import React, { useEffect, useMemo, useState } from "react";
import Icon from "./Icon";
import { MissionAreaArt } from "./AdventureArt";
import { kidApi } from "../lib/api";

// Kid-friendly chore list: Folders (default), Tabs, or the plain List.
// Folders come from the template catalog (title -> category -> one of 6 folders),
// then the chore's icon, then "Other". No stored category is needed.
export const KID_VIEW_KEY = "cm_kid_chore_view";
export const KID_VIEWS = [["folders", "Folders"], ["tabs", "Tabs"], ["list", "List"]];

const GROUPS = [
  { key: "room", label: "My Room", emoji: "🛏️", cats: ["Bedroom", "Bedtime", "Morning", "Self Care", "Laundry"] },
  { key: "kitchen", label: "Kitchen", emoji: "🍽️", cats: ["Kitchen", "Cooking"] },
  { key: "home", label: "Home Helper", emoji: "🏠", cats: ["Bathroom", "Living Room", "Trash", "Helping", "Errands"] },
  { key: "outside", label: "Outside & Pets", emoji: "🌳", cats: ["Outdoor", "Garden", "Seasonal", "Pets", "Weekend"] },
  { key: "brain", label: "Brain & Fun", emoji: "📚", cats: ["School", "Screens", "Tech Help", "Music & Art", "Money"] },
  { key: "kind", label: "Kind & Healthy", emoji: "💛", cats: ["Kindness", "Manners", "Health", "Siblings & Baby"] },
];
const OTHER = { key: "other", label: "Other", emoji: "📦" };
const ALL_GROUPS = [...GROUPS, OTHER];

const CAT_TO_GROUP = {};
GROUPS.forEach((g) => g.cats.forEach((c) => { CAT_TO_GROUP[c] = g.key; }));

const ICON_TO_GROUP = {
  bed: "room", shirt: "room", bath: "room", smile: "room", "toy-brick": "room",
  utensils: "kitchen", cookie: "kitchen", carrot: "kitchen", sandwich: "kitchen",
  brush: "home", "trash-2": "home",
  dog: "outside", cat: "outside", bone: "outside", flower: "outside", sprout: "outside",
  leaf: "outside", car: "outside", snowflake: "outside", droplet: "outside",
  book: "brain", backpack: "brain", music: "brain", piano: "brain", palette: "brain", "gamepad-2": "brain",
  heart: "kind", bike: "kind", star: "kind",
};

const TABS = [["daily", "Every day"], ["weekly", "This week"], ["once", "Just once"]];

export function groupKeyFor(chore, catByTitle) {
  const cat = catByTitle && catByTitle.get(String(chore.title || "").toLowerCase());
  if (cat && CAT_TO_GROUP[cat]) return CAT_TO_GROUP[cat];
  return ICON_TO_GROUP[chore.icon] || "other";
}

export default function KidChoreList({ todo, waiting, view = "folders", renderChore, renderWaiting }) {
  const [catByTitle, setCatByTitle] = useState(null);
  const [open, setOpen] = useState(null);
  const [tabKey, setTabKey] = useState(null);
  const [waitOpen, setWaitOpen] = useState(null);

  useEffect(() => {
    let live = true;
    kidApi.get("/chore-templates")
      .then(({ data }) => {
        if (!live) return;
        const m = new Map();
        (data.templates || []).forEach((t) => m.set(String(t.title).toLowerCase(), t.category));
        setCatByTitle(m);
      })
      .catch(() => { if (live) setCatByTitle(new Map()); });
    return () => { live = false; };
  }, []);

  const folders = useMemo(() => {
    const by = new Map();
    todo.forEach((c) => {
      const k = groupKeyFor(c, catByTitle);
      if (!by.has(k)) by.set(k, []);
      by.get(k).push(c);
    });
    return ALL_GROUPS.filter((g) => by.has(g.key)).map((g) => ({ g, items: by.get(g.key) }));
  }, [todo, catByTitle]);

  const tabs = useMemo(
    () => TABS.map(([k, l]) => ({ k, l, items: todo.filter((c) => (c.recurrence || "once") === k) })).filter((t) => t.items.length),
    [todo]
  );

  const list = (items) => (
    <div className="space-y-4">
      {items.map((c) => <React.Fragment key={c.id}>{renderChore(c)}</React.Fragment>)}
    </div>
  );

  let body = null;
  if (todo.length > 0) {
    if (view === "list") {
      body = list(todo);
    } else if (view === "tabs") {
      const cur = tabs.find((t) => t.k === tabKey) || tabs[0];
      body = (
        <div data-testid="kid-chore-tabs">
          <div className="flex gap-2 mb-4 flex-wrap">
            {tabs.map((t) => (
              <button key={t.k} type="button" onClick={() => setTabKey(t.k)} aria-pressed={cur.k === t.k} data-testid={`kid-chore-tab-${t.k}`}
                className={`px-4 py-2 rounded-full font-heading font-bold text-sm ${cur.k === t.k ? "bg-primary text-white" : "bg-white text-ink-soft border border-slate-200"}`}>
                {t.l} <span className="opacity-70">{t.items.length}</span>
              </button>
            ))}
          </div>
          {list(cur.items)}
        </div>
      );
    } else {
      const active = folders.find((f) => f.g.key === open);
      if (folders.length === 1) {
        body = list(folders[0].items);
      } else if (active) {
        body = (
          <div data-testid="kid-folder-open">
            <button type="button" onClick={() => setOpen(null)} data-testid="kid-folder-back"
              className="mb-3 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-heading font-bold text-sm text-ink shadow-sm">
              <span aria-hidden>←</span> All mission areas
            </button>
            <div className="font-heading font-black text-xl text-ink mb-3">{active.g.emoji} {active.g.label}</div>
            {list(active.items)}
          </div>
        );
      } else {
        body = (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="kid-folders">
            {folders.map(({ g, items }) => (
              <button key={g.key} type="button" onClick={() => setOpen(g.key)} data-testid={`kid-folder-${g.key}`}
                className={`mission-area mission-area-${g.key}`}>
                <MissionAreaArt area={g.key} />
                <div className="font-heading font-extrabold text-ink leading-tight">{g.label}</div>
                <div className="mission-area-meta"><span>{items.length} to do</span><span><Icon name="zap" width={12} height={12} /> {items.reduce((sum, c) => sum + c.points, 0)}</span></div>
                <span className="mission-area-arrow" aria-hidden="true"><Icon name="arrow-up-right" width={18} height={18} /></span>
              </button>
            ))}
          </div>
        );
      }
    }
  }

  const showWaiting = waitOpen === null ? waiting.length <= 2 : waitOpen;
  return (
    <div className="space-y-4" data-testid="kid-chore-list">
      {body}
      {waiting.length > 0 && (
        <div data-testid="kid-waiting-section">
          {waiting.length > 2 && (
            <button type="button" onClick={() => setWaitOpen(!showWaiting)} data-testid="kid-waiting-toggle"
              className="mb-3 w-full rounded-2xl bg-white/70 px-4 py-3 text-left font-heading font-bold text-ink-soft">
              ⏳ Waiting for grown-up ({waiting.length}) {showWaiting ? "▲" : "▼"}
            </button>
          )}
          {showWaiting && (
            <div className="space-y-4">
              {waiting.map((c) => <React.Fragment key={c.id}>{renderWaiting(c)}</React.Fragment>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
